import {
  useState,
  useEffect,
  createContext,
  useContext,
  type ReactNode,
} from "react";
import {
  supabase,
  getUserProfile,
  getOrganizationId,
  getOrgMembership,
  getOrgDetails,
  isOrgAdmin,
  isOrgSuperAdmin as isOrgSuperAdminCheck,
  isPlatformAdmin as isPlatformAdminCheck,
  canManageTeam as canManageTeamCheck,
  type OrgMembership,
  type OrgDetails,
  type OrgRole,
} from "../lib/supabase";
import type { Profile } from "../types/database.types";
import type { User, Session } from "@supabase/supabase-js";
import { setLastSignIn, type AuthMethod } from "../lib/authRecall";
import { logError, logWarn } from "../lib/logError";
import { forgetReturnTarget, markDeliberateSignOut } from "../lib/loginReturn";

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;

  // Organization context (SaaS multi-tenancy)
  organizationId: string | null;
  orgMembership: OrgMembership | null;
  orgDetails: OrgDetails | null;
  orgRole: OrgRole | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  canManageTeam: boolean;
  hasOrgAccess: boolean;

  // Actions
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithSSO: () => Promise<{ error: string | null }>;
  signOut: (opts?: { keepReturnTarget?: boolean }) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Organization state
  const [orgMembership, setOrgMembership] = useState<OrgMembership | null>(
    null
  );
  const [orgDetails, setOrgDetails] = useState<OrgDetails | null>(null);

  const organizationId = getOrganizationId();

  /**
   * Records how the user just signed in, for the login "Welcome back" UX.
   * Writes a per-device hint to localStorage immediately and persists the
   * canonical value to profiles.last_auth_method (best-effort, non-blocking).
   */
  const persistSignInMethod = async (signedInSession: Session) => {
    const provider =
      (signedInSession.user.app_metadata?.provider as string | undefined) ?? "email";
    const method: AuthMethod = provider === "azure" ? "microsoft" : "password";

    setLastSignIn({ method, email: signedInSession.user.email ?? undefined });

    try {
      await supabase
        .from("profiles")
        .update({
          last_auth_method: method,
          last_login_at: new Date().toISOString(),
        })
        .eq("id", signedInSession.user.id);
    } catch (err) {
      // Column may not exist yet pre-migration, or RLS may block, recall still
      // works via localStorage, so this is non-fatal.
      logWarn("[Auth] Could not persist last_auth_method:", err);
    }
  };

  const refreshProfile = async () => {
    try {
      const fetchedProfile = await getUserProfile();
      setProfile(fetchedProfile);
    } catch (err) {
      logError("[Auth] Error fetching profile:", err);
    }
  };

  const refreshOrgContext = async (userId: string) => {
    if (!organizationId) {
      console.warn("[Auth] No VITE_ORGANIZATION_ID configured, skipping org context");
      return;
    }

    try {
      // Fetch membership and org details in parallel
      const [membership, details] = await Promise.all([
        getOrgMembership(userId),
        getOrgDetails(),
      ]);

      setOrgMembership(membership);
      setOrgDetails(details);

      if (!membership) {
        console.warn("[Auth] User is not a member of the configured org");
      }
    } catch (err) {
      logError("[Auth] Error fetching org context:", err);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Get initial session
    const initializeAuth = async () => {
      try {
        console.log("[Auth] Initializing...");
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          logError("[Auth] getSession error:", error);
        }

        if (!isMounted) return;

                setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Fetch profile and org context in parallel
          await Promise.all([
            refreshProfile(),
            refreshOrgContext(session.user.id),
          ]);
        }
      } catch (err) {
        logError("[Auth] Initialization error:", err);
      } finally {
        if (isMounted) {
          console.log("[Auth] Loading complete");
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log(`[Auth] State changed: ${_event}`);
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      // IMPORTANT: Do NOT set loading=false for INITIAL_SESSION.
      // initializeAuth() already handles the initial load path with proper
      // await for profile+org context. Setting loading false here would
      // create a race condition where orgMembership is still null.
      if (_event !== 'INITIAL_SESSION') {
        setLoading(false);
        console.log("[Auth] Loading set to false after state change");
      }

      // On a fresh sign-in, remember the method used for the Welcome-back UX
      if (_event === "SIGNED_IN" && session?.user) {
        markDeliberateSignOut(false);
        persistSignInMethod(session);
      }

      // Fetch profile and org context in background (don't block rendering)
      if (session?.user) {
        Promise.all([
          refreshProfile(),
          refreshOrgContext(session.user.id),
        ]).then(() => {
          console.log("[Auth] Profile + org context refresh complete");
        });
      } else {
        setProfile(null);
        setOrgMembership(null);
        setOrgDetails(null);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithSSO = async (): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "azure",
        options: {
          redirectTo: window.location.origin,
          scopes: "email profile openid",
        },
      });

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (err) {
      return {
        error:
          err instanceof Error
            ? err.message
            : "An unexpected error occurred during Microsoft sign-in.",
      };
    }
  };

  // A deliberate sign-out forgets the return target so the next person on a
  // shared machine never inherits it. The idle timeout passes
  // keepReturnTarget: the same person is coming back to the same page.
  const signOut = async (opts?: { keepReturnTarget?: boolean }) => {
    if (!opts?.keepReturnTarget) {
      markDeliberateSignOut(true);
      forgetReturnTarget();
    }
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
    setOrgMembership(null);
    setOrgDetails(null);
  };

  // Computed values
  const orgRole = orgMembership?.role ?? null;
  const isAdmin = isOrgAdmin(orgMembership);
  const isSuperAdmin = isOrgSuperAdminCheck(orgMembership);
  const isPlatformAdminValue = isPlatformAdminCheck(profile);
  const canManageTeamValue = canManageTeamCheck(profile);

  // Access check: user has access if:
  //   1. They have an org membership record (primary check), OR
  //   2. They are a platform admin (always have access), OR
  //   3. They have legacy claims_access flag (transition period fallback)
  const hasOrgAccess = organizationId
    ? (orgMembership !== null || isPlatformAdminValue || (profile?.claims_access ?? false))
    : profile?.claims_access ?? false;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        organizationId,
        orgMembership,
        orgDetails,
        orgRole,
        isAdmin,
        isSuperAdmin,
        isPlatformAdmin: isPlatformAdminValue,
        canManageTeam: canManageTeamValue,
        hasOrgAccess,
        signIn,
        signInWithSSO,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- auto-suppressed for safety
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
